import { useEffect, useRef, useState } from 'react'

import { Input } from '@/app/components/Input'
import { useToolsSearch } from '@/app/hooks/data-hooks/useToolsSearch'
import { IToolSuggestion } from '@/types/tool-suggestion'
import Apps from '@mui/icons-material/Apps'
import SearchIcon from '@mui/icons-material/Search'
import { Typography } from '@/app/components/Typography'
import { useNamespacedIntl } from '../lib/useNamespacedIntl'

type Props = {
  onAddTool: (tool: IToolSuggestion) => void
  selectedIds: Set<string>
  disabled?: boolean
}

function resolveIconUrl(tool: IToolSuggestion): string | null {
  if (tool.logo?.url) return tool.logo.url
  const host = tool.domain?.replace(/^https?:\/\//i, '').split('/')[0]?.trim()
  if (!host) return null
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=64`
}

export function ToolSearchInput({ onAddTool, selectedIds, disabled }: Props) {
  const t = useNamespacedIntl('UserOnboarding')
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [brokenImages, setBrokenImages] = useState<Record<string, boolean>>({})
  const containerRef = useRef<HTMLDivElement>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300)
    return () => clearTimeout(timer)
  }, [query])

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const { data, isFetchingNextPage, hasNextPage, fetchNextPage, isLoading } =
    useToolsSearch(debouncedQuery, open)

  const tools = data?.pages.flatMap((p) => p.data) ?? []

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage()
        }
      },
      { threshold: 0.5 }
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleSelect = (tool: IToolSuggestion) => {
    onAddTool(tool)
    setOpen(false)
    setQuery('')
    setDebouncedQuery('')
  }

  return (
    <div ref={containerRef} className="relative w-full max-w-sm">
      <Input
        type="text"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => !disabled && setOpen(true)}
        placeholder={t('tools_search_placeholder')}
        disabled={disabled}
        className="w-full text-sm"
        startDecoration={
          <SearchIcon className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
        }
      />

      {open && (
        <div className="absolute left-0 right-0 top-full z-50 mt-1 max-h-48 overflow-y-auto rounded-xl border border-lines bg-white shadow-lg">
          {isLoading ? (
            <>
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-2 px-3 py-2">
                  <div className="h-7 w-7 shrink-0 animate-pulse rounded-md bg-gray-200" />
                  <div className="h-3.5 w-24 animate-pulse rounded bg-gray-200" />
                </div>
              ))}
            </>
          ) : tools.length === 0 ? (
            <div className="px-3 py-3 text-sm text-gray-500">
              {t('tools_search_empty')}
            </div>
          ) : (
            <>
              {tools.map((tool) => {
                const isSelected = selectedIds.has(tool._id)
                const imgUrl = brokenImages[tool._id]
                  ? null
                  : resolveIconUrl(tool)
                return (
                  <button
                    key={tool._id}
                    type="button"
                    onClick={() => handleSelect(tool)}
                    className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-gray-50"
                  >
                    {imgUrl ? (
                      <img
                        src={imgUrl}
                        alt=""
                        width={28}
                        height={28}
                        className="h-7 w-7 shrink-0 rounded-md object-contain"
                        onError={() =>
                          setBrokenImages((prev) => ({
                            ...prev,
                            [tool._id]: true,
                          }))
                        }
                      />
                    ) : (
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-gray-100 text-gray-500">
                        <Apps className="h-4 w-4" aria-hidden />
                      </span>
                    )}
                    <Typography variant="body2" className="flex-1 font-medium">
                      {tool.name}
                    </Typography>
                    {isSelected && (
                      <span className="text-xs font-semibold text-mydra-purple">
                        ✓
                      </span>
                    )}
                  </button>
                )
              })}
              <div ref={sentinelRef} className="h-1" />
              {isFetchingNextPage && (
                <div className="flex items-center gap-2 px-3 py-2">
                  <div className="h-7 w-7 shrink-0 animate-pulse rounded-md bg-gray-200" />
                  <div className="h-3.5 w-24 animate-pulse rounded bg-gray-200" />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
